export type ValidationResult<S extends string> = [
  boolean,
  Partial<Record<S, string[]>>
];
export type ValidationFunction<T, S extends string> = (
  object: T
) => ValidationResult<S>;

export interface ValidationCombinator {
  <T, S1 extends string>(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S1>
  ): ValidationFunction<T, S1>;
  <T, S1 extends string, S2 extends string>(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>
  ): ValidationFunction<T, S1 | S2>;
  <T, S1 extends string, S2 extends string, S3 extends string>(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>
  ): ValidationFunction<T, S1 | S2 | S3>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>
  ): ValidationFunction<T, S1 | S2 | S3 | S4>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>
  ): ValidationFunction<T, S1 | S2 | S3 | S4 | S5>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>
  ): ValidationFunction<T, S1 | S2 | S3 | S4 | S5 | S6>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>
  ): ValidationFunction<T, S1 | S2 | S3 | S4 | S5 | S6 | S7>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>
  ): ValidationFunction<T, S1 | S2 | S3 | S4 | S5 | S6 | S7 | S8>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string,
    S9 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>,
    v9: ValidationFunction<T, S9>
  ): ValidationFunction<T, S1 | S2 | S3 | S4 | S5 | S6 | S7 | S8 | S9>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string,
    S9 extends string,
    S10 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>,
    v9: ValidationFunction<T, S9>,
    v10: ValidationFunction<T, S10>
  ): ValidationFunction<T, S1 | S2 | S3 | S4 | S5 | S6 | S7 | S8 | S9 | S10>;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string,
    S9 extends string,
    S10 extends string,
    S11 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>,
    v9: ValidationFunction<T, S9>,
    v10: ValidationFunction<T, S10>,
    v11: ValidationFunction<T, S11>
  ): ValidationFunction<
    T,
    S1 | S2 | S3 | S4 | S5 | S6 | S7 | S8 | S9 | S10 | S11
  >;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string,
    S9 extends string,
    S10 extends string,
    S11 extends string,
    S12 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>,
    v9: ValidationFunction<T, S9>,
    v10: ValidationFunction<T, S10>,
    v11: ValidationFunction<T, S11>,
    v12: ValidationFunction<T, S12>
  ): ValidationFunction<
    T,
    S1 | S2 | S3 | S4 | S5 | S6 | S7 | S8 | S9 | S10 | S11 | S12
  >;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string,
    S9 extends string,
    S10 extends string,
    S11 extends string,
    S12 extends string,
    S13 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>,
    v9: ValidationFunction<T, S9>,
    v10: ValidationFunction<T, S10>,
    v11: ValidationFunction<T, S11>,
    v12: ValidationFunction<T, S12>,
    v13: ValidationFunction<T, S13>
  ): ValidationFunction<
    T,
    S1 | S2 | S3 | S4 | S5 | S6 | S7 | S8 | S9 | S10 | S11 | S12 | S13
  >;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string,
    S9 extends string,
    S10 extends string,
    S11 extends string,
    S12 extends string,
    S13 extends string,
    S14 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>,
    v9: ValidationFunction<T, S9>,
    v10: ValidationFunction<T, S10>,
    v11: ValidationFunction<T, S11>,
    v12: ValidationFunction<T, S12>,
    v13: ValidationFunction<T, S13>,
    v14: ValidationFunction<T, S14>
  ): ValidationFunction<
    T,
    S1 | S2 | S3 | S4 | S5 | S6 | S7 | S8 | S9 | S10 | S11 | S12 | S13 | S14
  >;
  <
    T,
    S1 extends string,
    S2 extends string,
    S3 extends string,
    S4 extends string,
    S5 extends string,
    S6 extends string,
    S7 extends string,
    S8 extends string,
    S9 extends string,
    S10 extends string,
    S11 extends string,
    S12 extends string,
    S13 extends string,
    S14 extends string,
    S15 extends string
  >(
    v1: ValidationFunction<T, S1>,
    v2: ValidationFunction<T, S2>,
    v3: ValidationFunction<T, S3>,
    v4: ValidationFunction<T, S4>,
    v5: ValidationFunction<T, S5>,
    v6: ValidationFunction<T, S6>,
    v7: ValidationFunction<T, S7>,
    v8: ValidationFunction<T, S8>,
    v9: ValidationFunction<T, S9>,
    v10: ValidationFunction<T, S10>,
    v11: ValidationFunction<T, S11>,
    v12: ValidationFunction<T, S12>,
    v13: ValidationFunction<T, S13>,
    v14: ValidationFunction<T, S14>,
    v15: ValidationFunction<T, S15>
  ): ValidationFunction<
    T,
    | S1
    | S2
    | S3
    | S4
    | S5
    | S6
    | S7
    | S8
    | S9
    | S10
    | S11
    | S12
    | S13
    | S14
    | S15
  >;
}

export const validationAll: ValidationCombinator = <T>(
  ...validators: any
): any => {
  return (object: T) => {
    let allValid = true;
    const brokenRules: Record<string, string[]> = {};

    for (const validator of validators) {
      const [isValid, rules] = validator(object);
      if (!isValid) {
        allValid = false;
        for (const [key, value] of Object.entries(rules)) {
          if (brokenRules[key] !== undefined) {
            brokenRules[key] = brokenRules[key].concat(value as string[]);
          } else {
            brokenRules[key] = value as string[];
          }
        }
      }
    }

    return [allValid, brokenRules];
  };
};

export const validationSome: ValidationCombinator = <T>(
  ...validators: any
): any => {
  return (object: T): any => {
    let anyValid = false;
    const brokenRules: Record<string, string[]> = {};

    for (const validator of validators) {
      const [isValid, rules] = validator(object);
      if (isValid) {
        anyValid = true;
      } else {
        for (const [key, value] of Object.entries(rules)) {
          if (brokenRules[key] !== undefined) {
            brokenRules[key] = brokenRules[key].concat(value as string[]);
          } else {
            brokenRules[key] = value as string[];
          }
        }
      }
    }

    return [anyValid, brokenRules];
  };
};
